// Customer Store — Single source of truth for the booking customer's contact details.
//
// This store owns: email, full name, phone number, country/calling code, form validity,
// and marketing subscription preferences. These fields were previously duplicated across
// Booking.ts and GiftVoucher.ts — do NOT add customer contact fields to those stores.
//
// Data lifecycle (CustomerDataState):
//   undefined -> User has not entered any data yet
//   Restored  -> Data was loaded from localStorage (previous session). Form pre-fills
//                from this but isValid is forced false until the user re-validates.
//   Dirty     -> User has edited a field. Data has not yet been confirmed by the API.
//   Synced    -> The API has accepted the current data (via booking or gift voucher update).
//
// Temporary coupling:
//   Customer data is currently synced to the backend as part of the Booking.updateBooking /
//   GiftVoucher.updateGiftVoucherDetails API calls — there is no dedicated customer API yet.
//   Those thunks read from appState.customer and dispatch CustomerActions.markSynced() on
//   success. When a dedicated customer sync endpoint is added, the markSynced() calls should
//   move to that new thunk and the customer reads should be removed from Booking/GiftVoucher.
//
// Pattern rules:
//   - Components dispatch CustomerActions for contact fields, never BookingActions/GiftVoucherActions
//   - Thunks that need customer data read from appState.customer, never from their own slice
//   - Only the API success path should dispatch markSynced()
//   - clearCustomer() exists but is not yet wired up — it should be called on booking/voucher
//     completion or start-over when a full customer reset is appropriate
import type { Action, Reducer } from 'redux';
import { IAnalyticsAction } from './';
import { GoogleTagManagerEvents } from '../helpers/googleTagManager';

// Data State
export enum CustomerDataState {
    Restored = 'restored',
    Dirty = 'dirty',
    Synced = 'synced',
}

// State
export interface ICustomerState {
    emailAddress?: string;
    fullName?: string;
    phoneNumber?: string;
    countryCode?: string;
    callingCode?: string;
    isValid: boolean;
    emailSubscribed: boolean;
    smsSubscribed: boolean;
    dataState?: CustomerDataState;
}

/**
 * Combines callingCode and phoneNumber into a single string for API calls.
 * Returns null if phoneNumber is empty/undefined.
 * Omits callingCode if it is undefined rather than producing "undefined 444444444".
 */
export function formatPhoneNumber(callingCode: string | undefined, phoneNumber: string | undefined): string | undefined {
    if (!phoneNumber) return undefined;
    return callingCode ? `${callingCode} ${phoneNumber}` : phoneNumber;
}

// Action Types
export const SET_EMAIL: 'CUSTOMER/SET_EMAIL' = 'CUSTOMER/SET_EMAIL';
export const SET_FULL_NAME: 'CUSTOMER/SET_FULL_NAME' = 'CUSTOMER/SET_FULL_NAME';
export const SET_PHONE_NUMBER: 'CUSTOMER/SET_PHONE_NUMBER' = 'CUSTOMER/SET_PHONE_NUMBER';
export const SET_EMAIL_SUBSCRIBED: 'CUSTOMER/SET_EMAIL_SUBSCRIBED' = 'CUSTOMER/SET_EMAIL_SUBSCRIBED';
export const SET_SMS_SUBSCRIBED: 'CUSTOMER/SET_SMS_SUBSCRIBED' = 'CUSTOMER/SET_SMS_SUBSCRIBED';
export const SET_VALID: 'CUSTOMER/SET_VALID' = 'CUSTOMER/SET_VALID';
export const CLEAR_CUSTOMER: 'CUSTOMER/CLEAR' = 'CUSTOMER/CLEAR';
export const MARK_CUSTOMER_SYNCED: 'CUSTOMER/MARK_SYNCED' = 'CUSTOMER/MARK_SYNCED';

// Action Interfaces
interface SetEmailAction extends IAnalyticsAction {
    type: typeof SET_EMAIL;
    emailAddress: string;
}

interface SetFullNameAction extends IAnalyticsAction {
    type: typeof SET_FULL_NAME;
    fullName: string;
}

interface SetPhoneNumberAction extends IAnalyticsAction {
    type: typeof SET_PHONE_NUMBER;
    phoneNumber: string;
    countryCode: string;
    callingCode: string;
}

interface SetEmailSubscribedAction extends Action {
    type: typeof SET_EMAIL_SUBSCRIBED;
    emailSubscribed: boolean;
}

interface SetSmsSubscribedAction extends Action {
    type: typeof SET_SMS_SUBSCRIBED;
    smsSubscribed: boolean;
}

interface SetValidAction extends Action {
    type: typeof SET_VALID;
    isValid: boolean;
}

interface ClearCustomerAction extends Action {
    type: typeof CLEAR_CUSTOMER;
}

interface MarkCustomerSyncedAction extends Action {
    type: typeof MARK_CUSTOMER_SYNCED;
}

type KnownAction =
    | SetEmailAction
    | SetFullNameAction
    | SetPhoneNumberAction
    | SetEmailSubscribedAction
    | SetSmsSubscribedAction
    | SetValidAction
    | ClearCustomerAction
    | MarkCustomerSyncedAction;

// Action Creators
export const actionCreators = {
    setEmail: (emailAddress: string): SetEmailAction => ({
        type: SET_EMAIL,
        emailAddress,
        googleTagManagerEvent: GoogleTagManagerEvents.RecordEmailAddress(),
    }),

    setFullName: (fullName: string): SetFullNameAction => ({
        type: SET_FULL_NAME,
        fullName,
        googleTagManagerEvent: GoogleTagManagerEvents.RecordFullName(),
    }),

    setPhoneNumber: (phoneNumber: string, countryCode: string, callingCode: string): SetPhoneNumberAction => ({
        type: SET_PHONE_NUMBER,
        phoneNumber,
        countryCode,
        callingCode,
        googleTagManagerEvent: GoogleTagManagerEvents.RecordPhoneNumber(),
    }),

    setEmailSubscribed: (emailSubscribed: boolean): SetEmailSubscribedAction => ({
        type: SET_EMAIL_SUBSCRIBED,
        emailSubscribed,
    }),

    setSmsSubscribed: (smsSubscribed: boolean): SetSmsSubscribedAction => ({
        type: SET_SMS_SUBSCRIBED,
        smsSubscribed,
    }),

    setValid: (isValid: boolean): SetValidAction => ({
        type: SET_VALID,
        isValid,
    }),

    // TODO: Not yet dispatched anywhere. Wire this up when booking/voucher completion
    // or start-over flows need to fully reset the customer identity for the next transaction.
    clearCustomer: (): ClearCustomerAction => ({
        type: CLEAR_CUSTOMER,
    }),

    markSynced: (): MarkCustomerSyncedAction => ({
        type: MARK_CUSTOMER_SYNCED,
    }),
};

// Initial State
const initialState: ICustomerState = {
    isValid: false,
    emailSubscribed: false,
    smsSubscribed: false,
};

// Reducer
export const reducer: Reducer<ICustomerState> = (
    state: ICustomerState | undefined,
    incomingAction: Action
): ICustomerState => {
    if (state === undefined) {
        return initialState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case SET_EMAIL:
            return {
                ...state,
                emailAddress: action.emailAddress,
                dataState: CustomerDataState.Dirty,
            };

        case SET_FULL_NAME:
            return {
                ...state,
                fullName: action.fullName,
                dataState: CustomerDataState.Dirty,
            };

        case SET_PHONE_NUMBER:
            return {
                ...state,
                phoneNumber: action.phoneNumber,
                countryCode: action.countryCode,
                callingCode: action.callingCode,
                dataState: CustomerDataState.Dirty,
            };

        case SET_EMAIL_SUBSCRIBED:
            return {
                ...state,
                emailSubscribed: action.emailSubscribed,
            };

        case SET_SMS_SUBSCRIBED:
            return {
                ...state,
                smsSubscribed: action.smsSubscribed,
            };

        case SET_VALID:
            return {
                ...state,
                isValid: action.isValid,
            };

        case CLEAR_CUSTOMER:
            return { ...initialState };

        case MARK_CUSTOMER_SYNCED:
            return {
                ...state,
                dataState: CustomerDataState.Synced,
            };

        default:
            return state;
    }
};
